import * as React from "react";
import { useSelector } from "react-redux";
import {
  setParameters,
  removeParameter,
  clearParameters,
  setDateParameters,
} from "../redux/global/actions";
import {
  getSystemApplicationDetails,
  getSystemApplications,
  getSystemApplicationBulk,
} from "../redux/application/actions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import MultiValueInput from "../inputs/multiValueInput";
import {
  Grid,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useEffect } from "react";
import "./SystemApplications.css";
import SearchDateInput from "../inputs/searchDateInput";
import {
  downloadCSV,
  downloadJSON,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../utils";
import { DataTable, MaterialSymbol } from "@datalake/towercontrol-banner";
import ExportMenu from "../widgets/ExportMenu";

const SYSTEM_APPLICATION_COLUMNS = [
  {
    id: "application_id",
    label: "Application ID",
    filter: "text",
    nowrap: true,
  },
  { id: "creation_date", label: "Creation Date", nowrap: true },
  { id: "modification_date", label: "Modification Date", nowrap: true },
  { id: "sold_to", label: "Sold To", filter: "text" },
  { id: "business_partner", label: "Business Partner", filter: "text" },
  { id: "order", label: "Order ID", filter: "text" },
];

export default function SystemApplications(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const queryParameters = useSelector(
    (state) => state?.parameters?.systemApplications,
  );

  const [currentApplicationId, setCurrentApplicationId] = React.useState();
  const [currentBusinessPartner, setCurrentBusinessPartner] = React.useState();
  const [currentSoldTo, setCurrentSoldTo] = React.useState();
  const [currentOrder, setCurrentOrder] = React.useState();
  const [currentEndUser, setCurrentEndUser] = React.useState();
  const [currentSupportPartner, setCurrentSupportPartner] = React.useState();

  const handleExecuteSearch = () => {
    let queryObject = {
      limit: 100,
    };
    if (queryParameters?.applicationIds) {
      queryObject.application_id = queryParameters?.applicationIds;
    }
    if (queryParameters?.businessPartners) {
      queryObject.business_partner = queryParameters?.businessPartners;
    }
    if (queryParameters?.soldTos) {
      queryObject.sold_to = queryParameters?.soldTos;
    }
    if (queryParameters?.order) {
      queryObject.order = queryParameters?.order;
    }
    if (queryParameters?.endUser) {
      queryObject.end_user = queryParameters?.endUser;
    }
    if (queryParameters?.supportPartner) {
      queryObject.support_partner = queryParameters?.supportPartner;
    }
    if (
      queryParameters?.creationDateStart &&
      queryParameters?.creationDateEnd
    ) {
      queryObject.creation_date_from = queryParameters?.creationDateStart;
      queryObject.creation_date_to = queryParameters?.creationDateEnd;
    }
    dispatch(getSystemApplications(queryObject));
  };

  const handleExecuteBulkSearch = () => {
    let queryObject = {};
    if (queryParameters?.applicationIds) {
      queryObject.application_id = queryParameters?.applicationIds;
    }
    if (queryParameters?.businessPartners) {
      queryObject.business_partner = queryParameters?.businessPartners;
    }
    if (queryParameters?.soldTos) {
      queryObject.sold_to = queryParameters?.soldTos;
    }
    if (queryParameters?.order) {
      queryObject.order = queryParameters?.order;
    }
    if (queryParameters?.endUser) {
      queryObject.end_user = queryParameters?.endUser;
    }
    if (queryParameters?.supportPartner) {
      queryObject.support_partner = queryParameters?.supportPartner;
    }
    if (
      queryParameters?.creationDateStart &&
      queryParameters?.creationDateEnd
    ) {
      queryObject.creation_date_from = queryParameters?.creationDateStart;
      queryObject.creation_date_to = queryParameters?.creationDateEnd;
    }
    const saml_token = getLocalStorageSAMLToken();
    const infoToken = extractUserInfoFromToken(saml_token);
    queryObject.email = infoToken.email;
    queryObject.request_user = infoToken.username;
    dispatch(getSystemApplicationBulk(queryObject));
  };

  function handleSelectSystemApplication(applicationId) {
    dispatch(getSystemApplicationDetails(applicationId));
    navigate("/systemapplications/details/" + applicationId);
  }

  const handleDeleteApplicationIds = (chipToDelete) => () => {
    dispatch(
      removeParameter({
        systemApplications: { applicationIds: [chipToDelete] },
      }),
    );
  };

  const handleClearApplicationIds = () => {
    dispatch(clearParameters({ systemApplications: "applicationIds" }));
  };

  const handleDeleteBusinessPartner = (chipToDelete) => () => {
    dispatch(
      removeParameter({
        systemApplications: { businessPartners: [chipToDelete] },
      }),
    );
  };

  const handleClearBusinessPartners = () => {
    dispatch(clearParameters({ systemApplications: "businessPartners" }));
  };

  const handleDeleteSoldTo = (chipToDelete) => () => {
    dispatch(
      removeParameter({ systemApplications: { soldTos: [chipToDelete] } }),
    );
  };

  const handleClearSoldTo = () => {
    dispatch(clearParameters({ systemApplications: "soldTos" }));
  };

  const handleDeleteOrder = (chipToDelete) => () => {
    dispatch(
      removeParameter({ systemApplications: { order: [chipToDelete] } }),
    );
  };

  const handleClearOrder = () => {
    dispatch(clearParameters({ systemApplications: "order" }));
  };

  const handleDeleteEndUser = (chipToDelete) => () => {
    dispatch(
      removeParameter({ systemApplications: { endUser: [chipToDelete] } }),
    );
  };

  const handleClearEndUser = () => {
    dispatch(clearParameters({ systemApplications: "endUser" }));
  };

  const handleDeleteSupportPartner = (chipToDelete) => () => {
    dispatch(
      removeParameter({
        systemApplications: { supportPartner: [chipToDelete] },
      }),
    );
  };

  const handleClearSupportPartner = () => {
    dispatch(clearParameters({ systemApplications: "supportPartner" }));
  };

  // const handleExportToCSV = (materials) => {
  //   dispatch(exportMaterialsToCSV(materials))
  // };

  // const handleExportContractsToCSV = (materials) => {
  //   dispatch(exportMaterialContractsToCSV(materials))
  // };

  const systemApplications = useSelector((state) => {
    if (state.filteredSystemApplications) {
      return state.filteredSystemApplications.map((systemApplication) => {
        return {
          application_id: systemApplication?.application_id,
          application_family: systemApplication?.application_family,
          business_partner: systemApplication?.business_partner?.company_id,
          comments: systemApplication?.comments,
          creation_date: systemApplication?.creation_date,
          creation_note: systemApplication?.creation_note,
          description: systemApplication?.description,
          end_user: systemApplication?.end_user?.company_id,
          inactive_reason: systemApplication?.inactive_reason,
          modification_date: systemApplication?.modification_date,
          order: systemApplication?.order?.order_id,
          part_number: systemApplication?.part_number,
          product: systemApplication?.product,
          sold_to: systemApplication?.sold_to?.applicant_id,
          status: systemApplication?.status,
          support_partner: systemApplication?.support_partner?.company_id,
          type: systemApplication?.type,
          // children: systemApplication?.children
        };
      });
    } else return [];
  });

  const handleExport = (rows, format = "csv") => {
    const fields = [
      { name: "application_id" },
      { name: "application_family" },
      { name: "type" },
      { name: "status" },
      { name: "creation_date" },
      { name: "modification_date" },
      { name: "sold_to" },
      { name: "business_partner" },
      { name: "end_user" },
      { name: "support_partner" },
      { name: "order" },
      { name: "part_number" },
      { name: "product" },
      { name: "description" },
    ];
    if (format === "csv") {
      downloadCSV(fields, "system_applications_export", ",", rows);
    } else {
      downloadJSON(fields, "system_applications_export", rows);
    }
  };

  const [openBulkConfirmation, setOpenBulkConfirmation] = React.useState(false);
  const [openBulkMessage, setOpenBulkMessage] = React.useState(false);

  const handleKeydown = (event) => {
    if (event.key == "Enter") {
      event.stopPropagation();
      event.preventDefault();
      setTimeout(() => {
        handleExecuteSearch();
      }, 0);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  return (
    <React.Fragment>
      <Dialog open={openBulkMessage} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            Your query has been sent. You can follow the status in the Bulk
            Report page or wait for the Rainbow message that will announce the
            end of the export.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setOpenBulkMessage(false);
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openBulkConfirmation} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            This query could take a few minutes, are you sure you want to
            continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              handleExecuteBulkSearch();
              setOpenBulkConfirmation(false);
              setOpenBulkMessage(true);
            }}
          >
            Yes
          </Button>

          <Button variant="text" onClick={() => setOpenBulkConfirmation(false)}>
            No
          </Button>
        </DialogActions>
      </Dialog>
      <MultiValueInput
        setCurrentParam={setCurrentApplicationId}
        setQueryParams={setParameters}
        currentParam={currentApplicationId}
        queryParams={queryParameters?.applicationIds}
        handleDelete={handleDeleteApplicationIds}
        handleClear={handleClearApplicationIds}
        label="Application ID"
        family="systemApplications"
        type="applicationIds"
      />
      <MultiValueInput
        setCurrentParam={setCurrentBusinessPartner}
        setQueryParams={setParameters}
        currentParam={currentBusinessPartner}
        queryParams={queryParameters?.businessPartners}
        handleDelete={handleDeleteBusinessPartner}
        handleClear={handleClearBusinessPartners}
        label="Business Partner"
        family="systemApplications"
        type="businessPartners"
      />
      <MultiValueInput
        setCurrentParam={setCurrentSoldTo}
        setQueryParams={setParameters}
        currentParam={currentSoldTo}
        queryParams={queryParameters?.soldTos}
        handleDelete={handleDeleteSoldTo}
        handleClear={handleClearSoldTo}
        label="Sold To"
        family="systemApplications"
        type="soldTos"
      />
      <MultiValueInput
        setCurrentParam={setCurrentOrder}
        setQueryParams={setParameters}
        currentParam={currentOrder}
        queryParams={queryParameters?.order}
        handleDelete={handleDeleteOrder}
        handleClear={handleClearOrder}
        label="SAP Order"
        family="systemApplications"
        type="order"
      />
      <MultiValueInput
        setCurrentParam={setCurrentEndUser}
        setQueryParams={setParameters}
        currentParam={currentEndUser}
        queryParams={queryParameters?.endUser}
        handleDelete={handleDeleteEndUser}
        handleClear={handleClearEndUser}
        label="End Customer ID"
        family="systemApplications"
        type="endUser"
      />
      <MultiValueInput
        setCurrentParam={setCurrentSupportPartner}
        setQueryParams={setParameters}
        currentParam={currentSupportPartner}
        queryParams={queryParameters?.supportPartner}
        handleDelete={handleDeleteSupportPartner}
        handleClear={handleClearSupportPartner}
        label="Support Partner"
        family="systemApplications"
        type="supportPartner"
      />
      <Grid>
        <SearchDateInput
          title={"Creation Date"}
          setFilterDate={setDateParameters}
          family="systemApplications"
          type="creationDateStart"
          type2="creationDateEnd"
        />
      </Grid>
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<MaterialSymbol name="search" size="button" />}
          onClick={handleExecuteSearch}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<MaterialSymbol name="cloud_download" size="button" />}
          onClick={() => setOpenBulkConfirmation(true)}
        >
          Bulk Export
        </Button>
      </Stack>
      {systemApplications.length > 0 && (
        <DataTable
          title="System Applications"
          subtitle={
            systemApplications.length === 100
              ? `${systemApplications.length} results, use bulk export for more`
              : `${systemApplications.length} results`
          }
          columns={SYSTEM_APPLICATION_COLUMNS}
          rows={systemApplications}
          getRowId={(row, i) => `${row?.application_id}-${i}`}
          onRowClick={(row) =>
            handleSelectSystemApplication(row?.application_id)
          }
          defaultRowsPerPage={25}
          size="small"
          actions={(rows) => (
            <ExportMenu
              disabled={!rows.length}
              onExport={(format) => handleExport(rows, format)}
            />
          )}
          sx={{ mt: 3 }}
        />
      )}
    </React.Fragment>
  );
}
